import { z } from 'zod'
import { structuredContextSchema } from './context.js'
import { FINDING_STATUSES, findingCreateSchema } from './findings.js'
import { cardSchema, columnGroupSchema, columnSchema, headingNotesSchema, isoSchema, linkSyncSchema, rowSchema, swimlaneSchema, titleSchema, uuidSchema } from './schemas.js'

/**
 * Semantic board operations. This is the unit of undo, of autosave and of the
 * stage 2 `POST /api/boards/:id/operations` batch. Destinations are expressed as
 * `beforeId` (or null to append) rather than absolute positions, so the server can
 * recompact sibling lists without the client replaying the whole board.
 */

/**
 * Create payloads omit `position`: the destination is expressed as `beforeId`, so the
 * applier derives positions and recompacts only the affected sibling list. Card creates
 * also omit `updatedAt` (stamped on apply) and accept an optional `createdAt` so undoing
 * a delete restores the original creation time rather than inventing a new one.
 */
export const columnGroupCreateSchema = columnGroupSchema
export const columnCreateSchema = columnSchema.omit({ position: true })
export const swimlaneCreateSchema = swimlaneSchema.omit({ position: true })
export const rowCreateSchema = rowSchema.omit({ position: true })
export const cardCreateSchema = cardSchema
  .omit({ position: true, updatedAt: true })
  .extend({ createdAt: isoSchema.optional() })

/** Fields the card editor and inline title editing may commit. */
export const cardPatchSchema = cardSchema
  .pick({
    title: true,
    description: true,
    type: true,
    status: true,
    priority: true,
    moscow: true,
    owner: true,
    tags: true,
    colour: true,
    icon: true,
    estimateMinutes: true,
    dueDate: true,
    release: true,
    intent: true,
    context: true,
    relationships: true,
    sourcePlacement: true,
  })
  .partial()

const beforeId = uuidSchema.nullable()

export const operationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('board.rename'), name: titleSchema }),
  z.object({ type: z.literal('board.setContext'), context: structuredContextSchema }),

  z.object({ type: z.literal('group.create'), group: columnGroupCreateSchema }),
  z.object({ type: z.literal('group.rename'), id: uuidSchema, title: titleSchema }),
  z.object({ type: z.literal('group.setNotes'), id: uuidSchema, notes: headingNotesSchema }),
  z.object({ type: z.literal('group.delete'), id: uuidSchema }),

  z.object({ type: z.literal('column.create'), column: columnCreateSchema, beforeId }),
  z.object({ type: z.literal('column.rename'), id: uuidSchema, title: titleSchema }),
  z.object({ type: z.literal('column.setNotes'), id: uuidSchema, notes: headingNotesSchema }),
  z.object({ type: z.literal('column.setGroup'), id: uuidSchema, groupId: uuidSchema.nullable() }),
  z.object({ type: z.literal('column.move'), id: uuidSchema, beforeId }),
  z.object({ type: z.literal('column.setContext'), id: uuidSchema, context: structuredContextSchema }),
  z.object({ type: z.literal('column.delete'), id: uuidSchema }),

  z.object({ type: z.literal('swimlane.create'), swimlane: swimlaneCreateSchema, beforeId }),
  z.object({ type: z.literal('swimlane.rename'), id: uuidSchema, title: titleSchema }),
  z.object({ type: z.literal('swimlane.setNotes'), id: uuidSchema, notes: headingNotesSchema }),
  z.object({ type: z.literal('swimlane.move'), id: uuidSchema, beforeId }),
  z.object({ type: z.literal('swimlane.setContext'), id: uuidSchema, context: structuredContextSchema }),
  z.object({ type: z.literal('swimlane.delete'), id: uuidSchema }),

  z.object({ type: z.literal('row.create'), row: rowCreateSchema, beforeId }),
  z.object({ type: z.literal('row.rename'), id: uuidSchema, title: titleSchema }),
  z.object({ type: z.literal('row.setNotes'), id: uuidSchema, notes: headingNotesSchema }),
  z.object({ type: z.literal('row.move'), id: uuidSchema, swimlaneId: uuidSchema, beforeId }),
  z.object({ type: z.literal('row.setContext'), id: uuidSchema, context: structuredContextSchema }),
  z.object({ type: z.literal('row.delete'), id: uuidSchema }),

  z.object({ type: z.literal('card.create'), card: cardCreateSchema, beforeId }),
  z.object({ type: z.literal('card.update'), id: uuidSchema, patch: cardPatchSchema }),
  z.object({ type: z.literal('card.move'), id: uuidSchema, rowId: uuidSchema, columnId: uuidSchema, beforeId }),
  z.object({ type: z.literal('card.delete'), id: uuidSchema }),

  z.object({ type: z.literal('finding.create'), finding: findingCreateSchema }),
  z.object({ type: z.literal('finding.resolve'), id: uuidSchema, status: z.enum(FINDING_STATUSES) }),
])

/** Names used by token permission pickers and API enforcement. */
export const OPERATION_TYPES = operationSchema.options.map((option) => option.shape.type.value)

/**
 * Operations a token may be granted. Resolving a finding is kept for people: accepting one
 * turns an observation into intended architecture, which an agent must never do itself.
 */
export const INTEGRATION_OPERATION_TYPES = OPERATION_TYPES.filter((type) => type !== 'finding.resolve')

export const operationBatchSchema = z.object({
  mutationId: uuidSchema,
  expectedRevision: z.number().int().min(0),
  operations: z.array(operationSchema).min(1).max(2000),
  sync: linkSyncSchema.optional(),
})

export type Operation = z.infer<typeof operationSchema>
export type OperationBatch = z.infer<typeof operationBatchSchema>
export type CardPatch = z.infer<typeof cardPatchSchema>
